import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  ResponsiveContainer,
  Legend,
  Tooltip
} from 'recharts';
import {
  ArrowRight,
  Check,
  Target,
  Cog,
  BarChart3,
  Layers,
  Users,
  Sparkles,
  TrendingUp,
  Award,
  AlertTriangle,
  Minus,
  Loader2
} from 'lucide-react';
import { 
  INDUSTRY_BENCHMARKS, 
  calculatePercentile, 
  calculateCostOfInefficiency,
  getDimensionComparison
} from '../lib/benchmarks';
import { getTierColor, getTierSummary } from '../lib/scoring';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CAL_LINK = 'https://cal.com/sid-das/unpaved-demo?overlayCalendar=true';

const SharedResult = () => {
  const { resultId } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchResult = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/results/${resultId}`);
        if (!response.ok) {
          throw new Error('Result not found');
        }
        const data = await response.json();
        setResult(data.result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, [resultId]);

  const getIcon = (iconName) => {
    const icons = { Target, Cog, BarChart3, Layers, Users, Sparkles };
    const Icon = icons[iconName];
    return Icon ? <Icon className="w-5 h-5" strokeWidth={1.5} /> : null;
  };

  const getPositionIndicator = (position) => {
    switch (position) {
      case 'top_performer':
        return { icon: Award, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Top Performer' };
      case 'above_average':
        return { icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Above Average' };
      case 'below_average':
        return { icon: Minus, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Below Average' };
      case 'needs_attention':
        return { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', label: 'Needs Attention' };
      default:
        return { icon: Minus, color: 'text-zinc-600', bg: 'bg-zinc-50', label: 'Average' };
    }
  };

  const getBarColor = (color) => {
    switch (color) {
      case 'red': return '#ef4444';
      case 'amber': return '#f59e0b';
      case 'green': return '#10b981';
      default: return '#71717a';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-400 mx-auto mb-4" />
          <p className="text-zinc-600">Loading results...</p>
        </div>
      </div>
    );
  }

  if (error || !result) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <h1 className="font-serif text-3xl mb-4">Result Not Found</h1>
          <p className="text-zinc-600 mb-8">This result link may have expired or doesn't exist.</p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 bg-zinc-950 text-white px-6 py-3 text-sm font-medium hover:bg-zinc-800 transition-colors"
          >
            Take Your Own Assessment
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  const industry = result.industry || 'other';
  const companySize = result.company_size || '201-500';
  const benchmark = INDUSTRY_BENCHMARKS[industry] || INDUSTRY_BENCHMARKS.other;
  const overallScore = result.overall_score;
  const tier = result.tier;
  const tierColor = getTierColor(tier);
  const tierSummary = getTierSummary(tier);
  const dimensionScores = result.dimension_scores || [];

  const percentile = calculatePercentile(overallScore, industry);
  const costOfInefficiency = calculateCostOfInefficiency(overallScore, industry, companySize);

  const radarData = dimensionScores.map(d => {
    const dimBenchmark = benchmark.dimensions[d.dimensionId];
    return {
      dimension: d.dimensionId.charAt(0).toUpperCase() + d.dimensionId.slice(1),
      fullName: d.dimensionId,
      score: d.percentage,
      industryAvg: dimBenchmark?.avg || 50,
      fullMark: 100
    };
  });

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white border border-zinc-200 shadow-lg p-4 max-w-xs">
          <p className="font-semibold text-zinc-900 mb-2 capitalize">{data.fullName}</p>
          <div className="space-y-1 text-sm">
            <p className="flex justify-between">
              <span className="text-zinc-500">Score:</span>
              <span className="font-semibold text-zinc-900">{data.score}%</span>
            </p>
            <p className="flex justify-between">
              <span className="text-zinc-500">Industry Avg:</span>
              <span className="text-zinc-700">{data.industryAvg}%</span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="px-6 md:px-12 lg:px-24 py-6 border-b border-zinc-100">
        <div className="flex items-center justify-between">
          <a 
            href="https://unpaved.ai" 
            target="_blank" 
            rel="noopener noreferrer"
            className="font-serif text-xl tracking-tight hover:opacity-70 transition-opacity"
          >
            Unpaved
          </a>
          <div className="flex items-center gap-4">
            <span className="text-xs font-medium tracking-wide uppercase text-zinc-400 border border-zinc-200 px-3 py-1.5">
              Shared Result
            </span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-6 md:px-12 lg:px-24 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto"
        >
          {/* Company Info */}
          <div className="text-center mb-8">
            <p className="text-sm text-zinc-500 mb-2">
              Results for <span className="font-medium text-zinc-700">{result.company}</span>
              {result.industry && <span> • {benchmark.name}</span>}
            </p>
          </div>

          {/* Score Hero Section */}
          <div className="text-center mb-12">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400 mb-4">
              AI Org Health Score
            </p>
            
            <div className="mb-4">
              <span 
                className="font-serif text-8xl sm:text-9xl tracking-tight"
                style={{ color: tierColor }}
              >
                {overallScore}
              </span>
              <span className="text-4xl text-zinc-300 font-serif">/100</span>
            </div>

            {/* Tier Badge */}
            <div
              className="inline-flex items-center px-5 py-2.5 text-sm font-semibold mb-6"
              style={{
                backgroundColor: `${tierColor}15`,
                color: tierColor,
                border: `2px solid ${tierColor}40`
              }}
            >
              {tier}
            </div>

            {/* Percentile Badge */}
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 bg-zinc-50 border border-zinc-200 px-4 py-2">
                <TrendingUp className="w-4 h-4 text-zinc-600" />
                <span className="text-sm text-zinc-700">
                  Top <span className="font-semibold text-zinc-900">{Math.round(100 - percentile)}%</span> of {benchmark.name} organisations
                </span>
              </div>
            </div>

            <p className="text-lg text-zinc-600 leading-relaxed max-w-2xl mx-auto">
              {tierSummary}
            </p>
          </div>

          {/* Key Metrics Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="border border-zinc-200 p-6 text-center">
              <div className="text-4xl font-serif text-zinc-900 mb-2">
                Top {Math.round(100 - percentile)}%
              </div>
              <p className="text-sm text-zinc-500">Industry Percentile</p>
            </div>

            <div className="border border-zinc-200 p-6 text-center bg-red-50/30">
              <div className="text-4xl font-serif text-red-600 mb-2">
                ${costOfInefficiency.low.toLocaleString()}–{costOfInefficiency.high.toLocaleString()}
              </div>
              <p className="text-sm text-zinc-500">Est. Annual Cost of Gaps</p>
            </div>

            <div className="border border-zinc-200 p-6 text-center bg-emerald-50/30">
              <div className="text-4xl font-serif text-emerald-600 mb-2">
                +{Math.max(0, benchmark.topQuartile - overallScore)}pts
              </div>
              <p className="text-sm text-zinc-500">Potential Improvement</p>
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Radar Chart */}
            <div className="border border-zinc-200 p-8">
              <h3 className="font-serif text-2xl tracking-tight mb-6">
                Dimension Breakdown
              </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
                    <PolarGrid stroke="#E4E4E7" />
                    <PolarAngleAxis
                      dataKey="dimension"
                      tick={{ fill: '#52525B', fontSize: 11, fontWeight: 500 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Radar
                      name="Industry Average"
                      dataKey="industryAvg"
                      stroke="#3b82f6"
                      fill="transparent"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                    />
                    <Radar
                      name="Score"
                      dataKey="score"
                      stroke="#09090B"
                      fill="#09090B"
                      fillOpacity={0.15}
                      strokeWidth={2.5}
                    />
                    <Legend />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Dimension Cards */}
            <div className="space-y-4">
              <h3 className="font-serif text-2xl tracking-tight mb-2">By Dimension</h3>
              {dimensionScores.map((dimension, index) => {
                const comparison = getDimensionComparison(dimension.dimensionId, dimension.percentage, industry);
                const positionInfo = getPositionIndicator(comparison?.position);
                const PositionIcon = positionInfo.icon;

                return (
                  <div
                    key={dimension.dimensionId}
                    className="border border-zinc-200 p-5"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div 
                          className="p-2 rounded"
                          style={{ backgroundColor: `${getBarColor(dimension.color)}15` }}
                        >
                          {getIcon(dimension.dimensionId === 'strategic' ? 'Target' : 
                                   dimension.dimensionId === 'process' ? 'Cog' :
                                   dimension.dimensionId === 'data' ? 'BarChart3' :
                                   dimension.dimensionId === 'tech' ? 'Layers' :
                                   dimension.dimensionId === 'people' ? 'Users' : 'Sparkles')}
                        </div>
                        <div>
                          <span className="font-medium text-zinc-900 capitalize">{dimension.dimensionId}</span>
                          <div className={`inline-flex items-center gap-1 ml-2 px-2 py-0.5 text-xs font-medium ${positionInfo.bg} ${positionInfo.color}`}>
                            <PositionIcon className="w-3 h-3" />
                            {positionInfo.label}
                          </div>
                        </div>
                      </div>
                      <span
                        className="text-2xl font-semibold"
                        style={{ color: getBarColor(dimension.color) }}
                      >
                        {dimension.percentage}%
                      </span>
                    </div>
                    
                    <div className="relative h-3 bg-zinc-100">
                      {comparison && (
                        <div 
                          className="absolute top-0 bottom-0 w-0.5 bg-blue-500 z-10"
                          style={{ left: `${comparison.industryAvg}%` }}
                        />
                      )}
                      <div
                        className="absolute top-0 bottom-0 left-0"
                        style={{ 
                          backgroundColor: getBarColor(dimension.color),
                          width: `${dimension.percentage}%`
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CTA Section */}
          <div className="border-2 border-zinc-950 p-8 md:p-12 text-center">
            <h3 className="font-serif text-3xl sm:text-4xl tracking-tight mb-4">
              Want Your Own Diagnostic?
            </h3>
            
            <p className="text-zinc-600 leading-relaxed max-w-2xl mx-auto mb-8">
              Take the free 4-minute assessment to discover where your organisation bleeds time and money — and get personalised AI transformation recommendations.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 bg-zinc-950 text-white px-8 py-4 text-sm font-medium hover:bg-zinc-800 transition-colors"
              >
                Take Your Assessment
                <ArrowRight className="w-4 h-4" />
              </button>
              
              <a
                href={CAL_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border-2 border-zinc-950 text-zinc-950 px-8 py-4 text-sm font-medium hover:bg-zinc-50 transition-colors"
              >
                Book a Deep-Dive
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Footer */}
          <footer className="text-center py-12 mt-12 border-t border-zinc-100">
            <p className="text-sm text-zinc-400">
              Powered by <a href="https://unpaved.ai" target="_blank" rel="noopener noreferrer" className="font-serif hover:text-zinc-600 transition-colors">Unpaved AI</a>
            </p>
          </footer>
        </motion.div>
      </main>
    </div>
  );
};

export default SharedResult;
